interface RoomDetailsParams {
  roomSqFt?: number;
  utilitiesShareDescription?: string;
  roomIsFurnished?: boolean;
  hasPrivateBath?: boolean;
}

class RoomDetails {
  roomSqFt: number;
  utilitiesShareDescription: string;
  roomIsFurnished: boolean;
  hasPrivateBath: boolean;

  constructor({
    roomSqFt = 0,
    utilitiesShareDescription = '',
    roomIsFurnished = false,
    hasPrivateBath = false,
  }: RoomDetailsParams = {}) {
    this.roomSqFt = roomSqFt;
    this.utilitiesShareDescription = utilitiesShareDescription;
    this.roomIsFurnished = roomIsFurnished;
    this.hasPrivateBath = hasPrivateBath;
  }
}

const pluralizeGenderType = (genderType: string, count: string): string => {
  const genderTypeLowerCase = genderType.toLowerCase();
  if (Number(count) === 1) {
    return genderType;
  } else if (genderTypeLowerCase === 'male' || genderTypeLowerCase === 'female') {
    return genderType + 's';
  } else if (genderTypeLowerCase === 'non-binary') {
    return genderType.slice(0, -1) + 'ies';
  } else {
    return genderType;
  }
};

interface GenderDetail {
  genderCount?: string;
  roommateGenderType?: string;
}

interface PetDetail {
  petCount?: string;
  petType?: string;
}

type RoommateDetail = GenderDetail & PetDetail;

const getInfo = (roommateDetails: Array<RoommateDetail>): string => {
  return roommateDetails
    .filter((detail) => {
      return (detail.genderCount && detail.genderCount !== '0') || (detail.petCount && detail.petCount !== '0');
    })
    .map((detail) => {
      const isRoommateInfo = Boolean(detail.roommateGenderType);
      const count = isRoommateInfo ? detail.genderCount : detail.petCount;
      const type = isRoommateInfo ? pluralizeGenderType(detail.roommateGenderType!, count!) : detail.petType;

      return `${count} ${type}`;
    })
    .join(', ');
};

interface RoommateDetailsParams {
  roommateGenderDetails?: Array<GenderDetail>;
  totalCount?: string;
  description?: string;
  roommatePetDetails?: Array<PetDetail>;
}

class RoommateDetails {
  roommatesCount: string;
  description: string;
  roommatesInfo: string;
  petsInfo: string;

  constructor({
    roommateGenderDetails = [],
    totalCount = '',
    description = '',
    roommatePetDetails = [],
  }: RoommateDetailsParams = {}) {
    this.roommatesCount = totalCount;
    this.description = description;
    this.roommatesInfo = getInfo(roommateGenderDetails);
    this.petsInfo = getInfo(roommatePetDetails);
  }
}

interface RoomForRentParams {
  roomDetails?: RoomDetailsParams;
  roommateDetails?: RoommateDetailsParams;
  roommateRequirements?: {
    genderTypes?: Array<string>;
  };
}

class RoomForRent {
  roomDetails: RoomDetails;
  roommateDetails: RoommateDetails;
  preferredNewRoommate: string;

  constructor(roomForRent?: RoomForRentParams) {
    const { roomDetails = {}, roommateDetails = {}, roommateRequirements = {} } = roomForRent ?? {};

    this.roomDetails = new RoomDetails(roomDetails);
    this.roommateDetails = new RoommateDetails(roommateDetails);
    this.preferredNewRoommate = (roommateRequirements.genderTypes ?? []).join(', ');
  }
}

export { RoomForRent, RoomDetails };
